// Per-user order/visibility for the desktop nav's page buttons, for the
// current organization (the server stores one copy per organization and sends
// only the current one).
//
// Preferences are applied AFTER NewNav's access gating, so they can only reorder
// or hide buttons the user already has. A saved key for a page the user cannot
// see is simply skipped.

// Versioned so a future shape change can ignore old copies; m8_ so logout clears it.
export const NAV_PREFERENCES_STORAGE_KEY = 'm8_nav_preferences_v1';
// The org those preferences belong to, sent back on save (see saveNavPreferences).
export const NAV_PREFERENCES_ORGANIZATION_STORAGE_KEY = 'm8_nav_preferences_organization_id';

// The Recapture upsell occupies the same slot as the real Recapture page, so a
// user who later gains access keeps the position they chose for it. Only the
// position is shared: hiding is per button, so hiding the sales entry never
// hides the page the org later buys. The Patient Engagement upsell gets no
// alias: its page is role-gated and the upsell org-flag-gated, so both can
// render at once, and two buttons can't share one key.
const NAV_KEY_ALIASES = { 'recapture-sales': 'patient-recapture' };

// Key for ordering. Hidden state uses the button's own `key`.
export const navPreferenceKey = (button) => NAV_KEY_ALIASES[button.key] || button.key;

// Inserts each key of `reference` that `list` lacks right after the key it
// follows in `reference` (or first, when nothing before it is in `list`).
export const slotInMissingKeys = (list, reference) => {
  const result = [...list];
  const placed = new Set(result);
  reference.forEach((key, index) => {
    if (placed.has(key)) return;
    let previous;
    for (let i = index - 1; i >= 0 && previous === undefined; i -= 1) {
      if (placed.has(reference[i])) previous = reference[i];
    }
    result.splice(previous === undefined ? 0 : result.indexOf(previous) + 1, 0, key);
    placed.add(key);
  });
  return result;
};

const uniqueStrings = (value) => (Array.isArray(value)
  ? [...new Set(value.filter((key) => typeof key === 'string' && key))]
  : []);

export const normalizeNavPreferences = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const order = uniqueStrings(raw.order);
  const hidden = uniqueStrings(raw.hidden);
  return order.length || hidden.length ? { order, hidden } : null;
};

export const readStoredNavPreferences = () => {
  try {
    return normalizeNavPreferences(JSON.parse(localStorage.getItem(NAV_PREFERENCES_STORAGE_KEY)));
  } catch {
    return null;
  }
};

export const readStoredNavPreferencesOrganizationId = () => {
  try {
    const id = Number(localStorage.getItem(NAV_PREFERENCES_ORGANIZATION_STORAGE_KEY));
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};

const storeOrRemove = (key, value) => {
  if (value === null || value === undefined) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, value);
  }
};

// Sign-in payloads pass the org id too; a save leaves it as it is.
export const storeNavPreferences = (preferences, organizationId) => {
  try {
    const normalized = normalizeNavPreferences(preferences);
    storeOrRemove(NAV_PREFERENCES_STORAGE_KEY, normalized && JSON.stringify(normalized));
    if (organizationId !== undefined) storeOrRemove(NAV_PREFERENCES_ORGANIZATION_STORAGE_KEY, organizationId);
  } catch {
    // Storage can be unavailable (private mode); the server copy still holds it.
  }
};

// Saved keys first, in saved order; anything not saved (a new page, a newly
// granted role) follows in default order.
export const orderNavButtons = (buttons, preferences) => {
  if (!preferences) return buttons;

  const byKey = new Map(buttons.map((button) => [navPreferenceKey(button), button]));
  const savedKeys = preferences.order.filter((key) => byKey.has(key));
  const saved = new Set(savedKeys);

  return [
    ...savedKeys.map((key) => byKey.get(key)),
    ...buttons.filter((button) => !saved.has(navPreferenceKey(button))),
  ];
};

export const applyNavPreferences = (buttons, preferences) => {
  const ordered = orderNavButtons(buttons, preferences);
  if (!preferences) return ordered;

  const hidden = new Set(preferences.hidden);
  const visible = ordered.filter((button) => !hidden.has(button.key));
  // A nav with no pages strands the user; show everything instead.
  return visible.length ? visible : ordered;
};
